import { useState, useEffect } from "react";
import { API } from "../../App";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Loader2, Users, CheckCircle2, AlertCircle, Send,
  UserPlus, DollarSign, Calendar, ChevronDown, Search, Mail
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

/**
 * ReEnrollmentDialog — bulk re-enrollment flow:
 * 1. Org admin selects a source program (current season)
 * 2. System shows all registered households
 * 3. Admin picks target program, confirms, sends invoices
 * 4. Parents receive email, pay, auto-register
 */
const ReEnrollmentDialog = ({ open, onOpenChange, program, programs, token }) => {
  const [step, setStep] = useState(1); // 1=select source, 2=preview, 3=confirm, 4=done
  const [sourceId, setSourceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [dueDays, setDueDays] = useState(14);
  const [customMessage, setCustomMessage] = useState("");
  const [selectedHouseholds, setSelectedHouseholds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  // Available source programs (exclude current target)
  const sourcePrograms = (programs || []).filter(
    (p) => p.program_id !== program.program_id
  );

  const resetDialog = () => {
    setStep(1);
    setSourceId("");
    setPreview(null);
    setCustomAmount("");
    setDueDays(14);
    setCustomMessage("");
    setSelectedHouseholds(new Set());
    setSelectAll(true);
    setSending(false);
    setResult(null);
    setSearchQuery("");
  };

  const handlePreview = async () => {
    if (!sourceId) {
      toast.error("Select a source program");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/org-invoices/bulk-re-enroll/preview`,
        {
          source_program_id: sourceId,
          target_program_id: program.program_id,
          custom_amount: customAmount ? parseFloat(customAmount) : null,
          due_days: dueDays,
        },
        { headers }
      );
      setPreview(res.data);
      // Auto-select all eligible
      const eligible = (res.data.households || [])
        .filter((h) => h.status === "eligible")
        .map((h) => h.household_id);
      setSelectedHouseholds(new Set(eligible));
      setSelectAll(true);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const selectedIds = Array.from(selectedHouseholds);
    if (selectedIds.length === 0) {
      toast.error("Select at least one household");
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(
        `${API}/org-invoices/bulk-re-enroll`,
        {
          source_program_id: sourceId,
          target_program_id: program.program_id,
          household_ids: selectedIds,
          custom_amount: customAmount ? parseFloat(customAmount) : null,
          due_days: dueDays,
          message: customMessage || null,
        },
        { headers }
      );
      setResult(res.data);
      setStep(4);
      toast.success(`${res.data.invoices_created} invoice(s) sent!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoices");
    } finally {
      setSending(false);
    }
  };

  const toggleHousehold = (id) => {
    setSelectedHouseholds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedHouseholds(new Set());
    } else {
      const eligible = (preview?.households || [])
        .filter((h) => h.status === "eligible")
        .map((h) => h.household_id);
      setSelectedHouseholds(new Set(eligible));
    }
    setSelectAll(!selectAll);
  };

  const filteredPreviewHouseholds = (preview?.households || []).filter((h) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      h.parent_name?.toLowerCase().includes(q) ||
      h.parent_email?.toLowerCase().includes(q) ||
      h.player_names?.some((n) => n.toLowerCase().includes(q))
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "eligible":
        return <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">Eligible</span>;
      case "already_invoiced":
        return <span className="px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full">Already Invoiced</span>;
      case "already_registered":
        return <span className="px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full">Already Registered</span>;
      case "no_email":
        return <span className="px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-full">No Email</span>;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetDialog();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-slate-600" />
            Invite Returning Players
          </DialogTitle>
          <DialogDescription>
            Send re-enrollment invoices to families from a previous program. When they pay, they're automatically registered.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 py-2">
          {[
            { n: 1, label: "Select Source" },
            { n: 2, label: "Review" },
            { n: 3, label: "Customize" },
            { n: 4, label: "Done" },
          ].map(({ n, label }) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                  step >= n
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
              </div>
              <span className={`text-xs ${step >= n ? "text-slate-700" : "text-slate-400"}`}>
                {label}
              </span>
              {n < 4 && <div className={`flex-1 h-px ${step > n ? "bg-slate-900" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select source program */}
        {step === 1 && (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">
                Which program are these players coming from?
              </label>
              <select
                className="w-full mt-2 px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
              >
                <option value="">Select previous program / season...</option>
                {sourcePrograms.map((p) => (
                  <option key={p.program_id} value={p.program_id}>
                    {p.name} ({p.current_participants || 0} players)
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Custom Amount (optional)</label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                    placeholder={`${program.price || 0} (program price)`}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Leave blank to use program price: ${program.price || 0}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Payment Deadline</label>
                <select
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                  value={dueDays}
                  onChange={(e) => setDueDays(parseInt(e.target.value))}
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={21}>21 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                disabled={!sourceId || loading}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                ) : (
                  <>Preview Families</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 2: Preview households */}
        {step === 2 && preview && (
          <div className="space-y-4 py-2">
            {/* Summary bar */}
            <div className="flex items-center gap-4 bg-slate-50 rounded-xl p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{preview.eligible}</p>
                <p className="text-xs text-slate-500">Eligible</p>
              </div>
              <div className="w-px h-10 bg-slate-200" />
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-900">{preview.total_households}</p>
                <p className="text-xs text-slate-500">Total Families</p>
              </div>
              <div className="w-px h-10 bg-slate-200" />
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">
                  ${(preview.target_price * preview.eligible).toLocaleString()}
                </p>
                <p className="text-xs text-slate-500">Potential Revenue</p>
              </div>
              {preview.already_registered > 0 && (
                <>
                  <div className="w-px h-10 bg-slate-200" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{preview.already_registered}</p>
                    <p className="text-xs text-slate-500">Already Registered</p>
                  </div>
                </>
              )}
            </div>

            {/* Search + select all */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search families..."
                  className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-slate-300"
                />
                Select All Eligible
              </label>
            </div>

            {/* Household list */}
            <div className="max-h-72 overflow-y-auto space-y-2 border border-slate-200 rounded-xl p-2">
              {filteredPreviewHouseholds.map((h) => (
                <div
                  key={h.household_id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    h.status !== "eligible"
                      ? "bg-slate-50 border-slate-100 opacity-60"
                      : selectedHouseholds.has(h.household_id)
                      ? "bg-blue-50 border-blue-200"
                      : "border-slate-200 hover:border-slate-300 cursor-pointer"
                  }`}
                  onClick={() => h.status === "eligible" && toggleHousehold(h.household_id)}
                >
                  {h.status === "eligible" && (
                    <input
                      type="checkbox"
                      checked={selectedHouseholds.has(h.household_id)}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 text-sm truncate">{h.parent_name}</p>
                      {getStatusBadge(h.status)}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{h.parent_email || "No email"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {h.player_names?.join(", ")} ({h.player_count} player{h.player_count !== 1 ? "s" : ""})
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-slate-700">${h.amount?.toFixed(2)}</p>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl">
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={selectedHouseholds.size === 0}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
              >
                Next: Customize ({selectedHouseholds.size} selected)
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Customize message + confirm */}
        {step === 3 && (
          <div className="space-y-4 py-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-900">
                    Sending {selectedHouseholds.size} invoice{selectedHouseholds.size !== 1 ? "s" : ""}
                  </p>
                  <p className="text-sm text-emerald-700">
                    For {preview?.target_program} — ${preview?.target_price?.toFixed(2)} each
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Custom Message (optional)</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                rows={3}
                placeholder="e.g. We're excited to have your family back for another season! Early bird pricing ends May 15th."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">This message will appear in the invoice email.</p>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
              <p className="font-medium text-slate-700">What happens next:</p>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium">1</span>
                </div>
                <p className="text-slate-600">Each family gets an email with their invoice</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-medium">2</span>
                </div>
                <p className="text-slate-600">They click "Pay" and complete payment via Stripe</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                </div>
                <p className="text-slate-600">
                  <strong>Their players are automatically registered</strong> in {preview?.target_program}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(2)} className="rounded-xl">
                Back
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending Invoices...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send {selectedHouseholds.size} Invoice{selectedHouseholds.size !== 1 ? "s" : ""}</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 4: Done */}
        {step === 4 && result && (
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Invoices Sent!</h3>
              <p className="text-slate-500 mt-1">
                {result.invoices_created} invoice{result.invoices_created !== 1 ? "s" : ""} sent to families from {result.source_program}
              </p>
            </div>

            {(result.skipped?.already_invoiced > 0 || result.skipped?.already_registered > 0 || result.skipped?.no_email > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-left">
                <p className="font-medium text-amber-800 mb-1">Skipped:</p>
                {result.skipped.already_invoiced > 0 && (
                  <p className="text-amber-700">{result.skipped.already_invoiced} already invoiced</p>
                )}
                {result.skipped.already_registered > 0 && (
                  <p className="text-amber-700">{result.skipped.already_registered} already registered</p>
                )}
                {result.skipped.no_email > 0 && (
                  <p className="text-amber-700">{result.skipped.no_email} missing email</p>
                )}
              </div>
            )}

            <Button
              onClick={() => {
                resetDialog();
                onOpenChange(false);
              }}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReEnrollmentDialog;
